import { copyObject } from '@/lib/objects'
import {
  GO_PRICING_MAIN_SHEET_PREFIX,
  GO_PRICING_SHEET_GO_RELEASE,
  GO_PRICING_SHEET_PRICING,
  GO_PRICING_SHEET_PROCUREMENT,
} from '@/components/expGoPricing/const'

function getFormatValue(value) {
  if (typeof value !== 'number' || Number.isInteger(value)) return value
  return Number(value).toFixed(2)
}

function getZeroDisplayFromNumFmt(cell) {
  const numFmt = String(cell?.numFmt ?? '').trim()
  if (!numFmt) return ''
  const parts = numFmt.split(';')
  const zeroPartRaw = (parts[2] || '').trim()
  if (!zeroPartRaw) return ''
  const zeroPart = zeroPartRaw.replace(/\\./g, '').replace(/"([^"]*)"/g, '$1').trim()
  if (!zeroPart) return ''
  if (zeroPart.includes('~')) return '~'
  if (zeroPart.includes('-')) return '-'
  if (/^0+(?:[.,]0+)?$/.test(zeroPart)) return '0'
  return ''
}

function formulaFallbackDisplay(cell) {
  const text = String(cell?.text ?? '').trim()
  if (text) return text
  const fmt = getZeroDisplayFromNumFmt(cell)
  if (fmt) return fmt
  // Когда в xlsx нет кэша вычисления формулы, безопаснее показать 0, чем пусто.
  return '0'
}

function cellToString(cell) {
  if (!cell || cell.value === undefined || cell.value === null) return ''

  const v = cell.value
  if (typeof v === 'object') {
    if (v.richText) {
      return v.richText.map(item => item.text || '').join(' ')
    }
    if (Object.prototype.hasOwnProperty.call(v, 'text') && v.text != null) {
      return String(v.text)
    }
    if (Object.prototype.hasOwnProperty.call(v, 'result')) {
      // Для формул и форматированных чисел отображаемое значение (text)
      // должно иметь приоритет, чтобы сохранялись символы вроде "-".
      const text = String(cell.text ?? '').trim()
      if (text) return text
      const r = v.result
      if (r !== undefined && r !== null) return String(getFormatValue(r))
      return ''
    }
    if (Object.prototype.hasOwnProperty.call(v, 'formula')) {
      const r = v.result
      if (r !== undefined && r !== null) return String(getFormatValue(r))
      return formulaFallbackDisplay(cell)
    }
    if (Object.prototype.hasOwnProperty.call(v, 'sharedFormula')) {
      const r = v.result
      if (r !== undefined && r !== null) return String(getFormatValue(r))
      return formulaFallbackDisplay(cell)
    }
    if (Object.prototype.hasOwnProperty.call(v, 'richText') && Array.isArray(v.richText)) {
      return v.richText.map(item => item.text || '').join(' ')
    }
    if (Object.prototype.hasOwnProperty.call(v, 'hyperlink') && v.hyperlink != null) {
      return String(v.text ?? v.hyperlink)
    }
  }
  if (cell.text != null && String(cell.text).trim() !== '') {
    return String(cell.text)
  }
  if (typeof v === 'number') return String(getFormatValue(v))
  return String(v)
}

/** @param {import('exceljs').Worksheet} sheet */
export function sheetToDisplayRows(sheet) {
  let detectedMaxCol = 0
  const maxRow = sheet.rowCount || 0
  for (let r = 1; r <= maxRow; r++) {
    const row = sheet.getRow(r)
    row.eachCell({ includeEmpty: false }, (_cell, col) => {
      if (col > detectedMaxCol) detectedMaxCol = col
    })
  }
  const maxCol = Math.min(
    Math.max(
      detectedMaxCol,
      sheet.actualColumnCount && sheet.actualColumnCount > 0
        ? sheet.actualColumnCount
        : 0,
      20
    ),
    80
  )
  const rows = []

  for (let r = 1; r <= maxRow; r++) {
    const row = sheet.getRow(r)
    const cells = []
    let any = false
    for (let c = 1; c <= maxCol; c++) {
      const cell = row.getCell(c)
      let master = cell
      try {
        if (cell.isMerged && cell.master) master = cell.master
      } catch {
        master = cell
      }
      let s = cellToString(master)
      // Иногда в merged-диапазонах значение (в т.ч. формула/результат) сидит в текущей ячейке.
      if (!String(s ?? '').trim() && master !== cell) {
        s = cellToString(cell)
      }
      const value = s != null ? String(s) : ''
      cells.push({
        value,
        merged: false,
        mergeObj: null,
      })
      if (value.trim()) any = true
    }
    if (any) rows.push(cells)
  }
  return rows
}

/**
 * Основной файл: три листа «Выпуск в ГО…».
 * @param {import('exceljs').Workbook} workbook
 */
export function parseGoPricingMainWorkbook(workbook) {
  const matched = []
  for (const ws of workbook.worksheets || []) {
    const m = String(ws.name || '').match(/\(ФВ-(\d+)\)$/i)
    if (!m) continue
    if (!String(ws.name || '').startsWith(GO_PRICING_MAIN_SHEET_PREFIX)) continue
    matched.push({
      order: Number(m[1]),
      name: ws.name,
      rows: sheetToDisplayRows(ws),
    })
  }
  matched.sort((a, b) => a.order - b.order)
  return {
    sheets: copyObject(
      matched.map(item => ({
        name: item.name,
        rows: item.rows,
      }))
    ),
  }
}

/**
 * Отдельный файл: один лист с заданным именем.
 * @param {import('exceljs').Workbook} workbook
 * @param {string} sheetName
 */
export function parseGoPricingSingleSheetWorkbook(workbook, sheetName) {
  const ws = workbook.getWorksheet(sheetName)
  if (!ws) {
    throw new Error(
      `В файле не найден лист «${sheetName}». Имя вкладки должно совпадать с этим названием.`
    )
  }
  return {
    name: sheetName,
    rows: copyObject(sheetToDisplayRows(ws)),
  }
}

export function parseGoPricingProcurementWorkbook(workbook) {
  return parseGoPricingSingleSheetWorkbook(
    workbook,
    GO_PRICING_SHEET_PROCUREMENT
  )
}

export function parseGoPricingGoReleaseWorkbook(workbook) {
  return parseGoPricingSingleSheetWorkbook(workbook, GO_PRICING_SHEET_GO_RELEASE)
}

export function parseGoPricingPricingWorkbook(workbook) {
  return parseGoPricingSingleSheetWorkbook(workbook, GO_PRICING_SHEET_PRICING)
}
